import { Loader } from '../Widgets';
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toastr } from 'react-redux-toastr'
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { Employee } from 'service/Models';
import ServerGateway from 'service/ServerGateway';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faDownload, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import Swal from 'sweetalert2';

export const Leave = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [month, setMonth] = useState<string>(moment().format("YYYY-MM"));
    const [employeeId, setEmployeeId] = useState<number>(0);
    const [leaves, setLeaves] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        loadLeaves();
    }, [month, employeeId]);

    const loadLeaves = () => {
        setIsLoading(true);
        const from = moment(month).startOf('month').format("YYYY-MM-DD");
        const to = moment(month).endOf('month').format("YYYY-MM-DD");
        ServerGateway
            .getLeaves(employeeId, "-1", from, to)
            .then((data) => {
                setLeaves(data);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const handleMonthChange = (e) => {
        setMonth(e.target.value);
    }

    const getClasNameByStatus = (status) => {
        if (status === "Requested") {
            return "badge bg-warning"
        }
        else if (status === "Approved") {
            return "badge bg-success"
        }
        else if (status === "Rejected") {
            return "badge bg-danger"
        }
    }

    const handleEmployeeSelection = (selectedValue) => {
        if(selectedValue) {
            setEmployeeId(selectedValue.value);
        }
        else {
            setEmployeeId(0);
        }
    }

    const handleEmployeeSearch = (inputValue: string) => {
        return ServerGateway.getEmployees(inputValue, 0, 1, 10)
            .then((data) => {
                return data.entries.map(function (employee) {
                    return {
                        value: employee.id,
                        label: (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `<b>${employee.name}</b><br /><i>${employee.employeeNumber}</i> - ${employee.groupName} - ${employee.designationName}`,
                                }}
                            />
                        ),
                    };
                });
            });
    }

    const approveLeave = async (request) => {
            // Step 1: Show confirmation dialog
            const confirmResult = await Swal.fire({
                title: 'Approve Leave Request?',
                text: 'Are you sure you want to approve this leave request?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, approve it!',
                cancelButtonText: 'Cancel'
            });
    
            if (!confirmResult.isConfirmed) {
                return; // User cancelled
            }
    
            // Step 2: Show comment input dialog
            const commentResult = await Swal.fire({
                title: 'Add Comment (Optional)',
                text: 'You can add a comment for this approval:',
                input: 'textarea',
                inputPlaceholder: 'Enter your comment here...',
                inputAttributes: {
                    'aria-label': 'Type your comment here',
                    'maxlength': '500' // Changed from number to string
                },
                showCancelButton: true,
                confirmButtonText: 'Approve',
                cancelButtonText: 'Cancel',
                inputValidator: (value) => {
                    if (value && value.length > 500) {
                        return 'Comment cannot exceed 500 characters';
                    }
                    return undefined; // Explicitly return undefined for valid input
                }
            });
    
            if (!commentResult.isConfirmed) {
                return; // User cancelled at comment stage
            }
    
            const comment = commentResult.value || ''; // Get comment or empty string
    
            // Step 3: Show loading state
            Swal.fire({
                title: 'Processing...',
                text: 'Approving leave request',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
            
            request.status = "Approved"; // Set status to Approved
            request.remarks = comment; // Add comment to request
            ServerGateway.saveLeaveRequest(request)
                .then(() => {
                    // Step 4: Close loading state
                    Swal.close();
    
                    // Step 5: Show success message
                    toastr.success("Success", "Leave request approved successfully.");
    
                    // Step 6: Reload data
                    loadLeaves();
                })
                .catch(error => {
                    console.error('Error approving leave:', error);
    
                    // Show error message
                    toastr.error("Error", "Failed to approve leave request.");
    
                    // Close loading state
                    Swal.close();
                });
        }
    
        const cancelLeave = async (request) => {
            // Step 1: Show confirmation dialog
            const confirmResult = await Swal.fire({
                title: 'Reject Leave Request?',
                text: 'Are you sure you want to reject this leave request?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, reject it!',
                cancelButtonText: 'Cancel'
            });
    
            if (!confirmResult.isConfirmed) {
                return; // User cancelled
            }
    
            // Step 2: Show comment input dialog
            const commentResult = await Swal.fire({
                title: 'Add Comment (Optional)',
                text: 'You can add a comment for this rejection:',
                input: 'textarea',
                inputPlaceholder: 'Enter your comment here...',
                inputAttributes: {
                    'aria-label': 'Type your comment here',
                    'maxlength': '500' // Changed from number to string
                },
                showCancelButton: true,
                confirmButtonText: 'Reject',
                cancelButtonText: 'Cancel',
                inputValidator: (value) => {
                    if (value && value.length > 500) {
                        return 'Comment cannot exceed 500 characters';
                    }
                    return undefined; // Explicitly return undefined for valid input
                }
            });
    
            if (!commentResult.isConfirmed) {
                return; // User cancelled at comment stage
            }
    
            const comment = commentResult.value || ''; // Get comment or empty string
    
            // Step 3: Show loading state
            Swal.fire({
                title: 'Processing...',
                text: 'Rejecting leave request',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
    
            request.status = "Rejected"; // Set status to Rejected
            request.remarks = comment; // Add comment to request
            ServerGateway.saveLeaveRequest(request)
                .then(() => {
                    // Step 4: Close loading state
                    Swal.close();
    
                    // Step 5: Show success message
                    toastr.success("Success", "Leave request rejected successfully.");
    
                    // Step 6: Reload data
                    loadLeaves();
                })
                .catch(error => {
                    console.error('Error approving leave:', error);
    
                    // Show error message
                    toastr.error("Error", "Failed to reject leave request.");
    
                    // Close loading state
                    Swal.close();
                });
        }

    return (
        <div className="page-leave">
            <div className='container'>
                <div className='row'>
                    <h2>Leaves</h2>
                </div>
                <div className='row mb-2'>
                    <input type="month" className="form-control" value={month} onChange={handleMonthChange} style={{ textAlign: "center" }}></input>
                </div>
                <div className='row mb-2'>
                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        isClearable
                        onChange={handleEmployeeSelection}
                        loadOptions={handleEmployeeSearch}
                        placeholder="Select an employee"
                        className="form-control p-0"
                        style={{ padding: 0 }}
                    />
                </div>
                <div className="row">
                    <div className="btn bg-primary" onClick={loadLeaves}>
                        Reload Data
                    </div>
                </div>
                <div className='row' style={{ marginTop: "20px" }}>
                    {isLoading &&
                        <Loader />
                    }
                    {!isLoading &&
                        <>
                            {(leaves.length == 0) &&
                                <div className="alert alert-secondary" role="alert">
                                    <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 10 }} />
                                    No leave request available
                                </div>
                            }
                            {(leaves.length > 0) &&
                                <ul className="list-group list-group-flush">
                                    {leaves && leaves.map((request) => (
                                        <li className="list-group-item card" style={{ marginBottom: "20px" }}>
                                            <ul className="list-group list-group-flush">
                                                <li className="list-group-item">
                                                    <div className="fw-bold" style={{ fontSize: "1.2rem" }}>
                                                        {request.employeeName}
                                                    </div>
                                                    <span>{request.leaveType.name} - {request.numberOfDays} days</span>
                                                </li>
                                                <li className="list-group-item">
                                                    {request.from != request.to &&
                                                        <span>From <b>{moment(request.from).format("DD/MM/YYYY, dddd")}</b> to <b>{moment(request.to).format("DD/MM/YYYY, dddd")}</b></span>
                                                    }
                                                    {request.from == request.to &&
                                                        <span>On <b>{moment(request.from).format("DD/MM/YYYY, dddd")}</b></span>
                                                    }
                                                </li>
                                                <li className="list-group-item">
                                                    Reason
                                                    <div className="fw-bold">
                                                        {request.reason}
                                                    </div>
                                                </li>
                                                <li className="list-group-item">
                                                    Status
                                                    <div className="fw-bold">
                                                        <div className={getClasNameByStatus(request.status)}>{request.status}</div>
                                                    </div>
                                                </li>                                                
                                                {request.status == "Requested" &&
                                                    <li className="list-group-item">
                                                        <div className="btn btn-success float-end" onClick={() => approveLeave(request)}>Approve</div>
                                                        <div className="btn btn-sm btn-danger" onClick={() => cancelLeave(request)}>Cancel</div>
                                                    </li>
                                                }
                                            </ul>
                                        </li>
                                    ))}
                                </ul>
                            }
                        </>
                    }
                </div>
            </div>
        </div>
    );
}